import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { 
    useDataProvider, 
    useNotify,
} from 'react-admin';

interface UnlinkHealthieModalProps {
    open: boolean;
    onClose: () => void;
    userId: number | string;
    userEmail: string;
    userName: string;
    healthieId: number | string;
    isProvider?: boolean;
    isAdmin?: boolean;
}

export const UnlinkHealthieModal: React.FC<UnlinkHealthieModalProps> = ({
    open,
    onClose,
    userId,
    userEmail,
    userName,
    healthieId,
    isProvider = false,
    isAdmin = false,
}) => {
    const [isUnlinking, setIsUnlinking] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const userType = isProvider ? 'provider' : isAdmin ? 'admin' : 'user';

    const handleConfirmUnlink = async () => {
        setIsUnlinking(true);
        try {
            // TODO: Implement unlinkHealthie API call once backend endpoint is ready
            // const response = await dataProvider.unlinkHealthie('users', {
            //     id: userId,
            // });

            // Temporary placeholder
            notify('Unlink feature coming soon - endpoint not yet implemented', { type: 'info' });
            
            // When implemented:
            // notify(`Successfully unlinked ${userName} from Healthie`, { type: 'success' });
            // onClose();
            // setTimeout(() => {
            //     window.location.reload();
            // }, 500);
        } catch (error: any) {
            const errorMessage = error.body?.message || error.message || 'Failed to unlink Healthie account';
            notify(errorMessage, { type: 'error' });
        } finally {
            setIsUnlinking(false);
        }
    };

    const handleClose = () => {
        if (!isUnlinking) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <WarningAmberIcon color="warning" />
                    <Typography variant="h6">Unlink from Healthie</Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Current Healthie ID:</strong> {healthieId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Email:</strong> {userEmail}
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="medium">
                        This only removes the connection in our system. The {userType}'s Healthie account remains active and unchanged.
                    </Typography>
                </Alert>

                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                    What will happen:
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="The Healthie ID connection is removed from our system"
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="Permission template sync will stop"
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="You can re-link this account later if needed"
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                </List>

                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                    What will NOT happen:
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <ListItemText 
                            primary={`The ${userType}'s Healthie account stays active`}
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="No deactivation or archiving in Healthie"
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <InfoOutlinedIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="Client access and appointments remain in Healthie"
                            primaryTypographyProps={{ variant: 'body2' }}
                        />
                    </ListItem>
                </List>

                <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        To deactivate or archive the {userType} in Healthie, you must do that separately in the Healthie admin panel.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={handleClose}
                    disabled={isUnlinking}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleConfirmUnlink}
                    variant="contained"
                    color="warning"
                    disabled={isUnlinking}
                    startIcon={isUnlinking ? <CircularProgress size={20} /> : null}
                >
                    {isUnlinking ? 'Unlinking...' : 'Unlink from Healthie'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

